<template>
  <b-modal
    id="inventory-view-add-ons-modal"
    title="Edit Add-Ons"
    body-class="py-4 px-5"
    content-class="bg-white"
    size="xl"
    centered
    scrollable
    @hidden="clearFormErrors"
  >
    <div id="form-container">
      <b-form
        v-for="(addOnCategory, categoryIndex) in addOns"
        :key="`add-on-category-${categoryIndex}`"
        class="mb-4 p-4 border rounded"
      >
        <b-form-group
          label-class="product-creation-labels"
          :state="validateState(categoryIndex, 'name')"
          :invalid-feedback="formErrors[categoryIndex].name"
        >
          <template slot="label">
            Add-On Name <small class="text-danger font-weight-bold">*</small>
          </template>
          <b-input
            :id="`add-on-category-name-${categoryIndex}`"
            v-model="addOnCategory.name"
            :maxlength="maxlengths.category"
          >
          </b-input>
        </b-form-group>
        <b-col md="6" class="pl-0">
          <b-form-group>
            <label for="allow_multiple_selection" class="d-flex align-items-center">
              <switches 
                v-model="addOnCategory.allow_multiple_selection" 
                theme="custom" 
                color="primary" 
                type-bold="true"
                :style="{ 'top': '15px' }"></switches>
                <span class="ml-2 mr-3 product-creation-labels mt-2">Allow multiple selection
                </span>
            </label>
            <b-form-group
              v-show="addOnCategory.allow_multiple_selection"
              class="pl-2"
              style="font-size: 13px; margin-left: 51px !important;"
            >
              <template slot="label">
                Set limit <span class="font-weight-bold text-danger">*</span>
              </template>
              <b-form-group
                  :state="validateState(categoryIndex, 'limit')"
                  :invalid-feedback="formErrors[categoryIndex].limit">
                <b-form-input
                  type="number"
                  v-model="addOnCategory.limit"
                >
                </b-form-input>
              </b-form-group>
            </b-form-group>
          </b-form-group>
        </b-col>
        <b-form-group
          label="Add-On Options"
          label-class="product-creation-labels"
        >
          <b-table
            :items="addOnCategory.add_ons"
            :fields="fields"
            class="border p-0 add-on-options"
            responsive
            hover
          >
            <template slot="name" slot-scope="data">
              <b-form-group
                class="mb-0"
                :state="validateState(categoryIndex, 'name', data.index)"
                :invalid-feedback="
                  formErrors[categoryIndex].add_ons[data.index].name
                "
              >
                <b-form-input
                  :id="`add-on-option-name-${data.index}`"
                  v-model="data.item.name"
                  :maxlength="maxlengths.option"
                >
                </b-form-input>
              </b-form-group>
            </template>
            <template slot="price" slot-scope="data">
              <b-form-group
                class="mb-0"
                :state="validateState(categoryIndex, 'price', data.index)"
                :invalid-feedback="
                  formErrors[categoryIndex].add_ons[data.index].price
                "
              >
                <b-input-group class="rounded border">
                  <template slot="prepend">
                    <b-input-group-text
                      class="bg-transparent d-inline-flex align-items-center px-2 border-0"
                    >
                      ₱
                    </b-input-group-text>
                  </template>
                  <currency-amount-input
                    v-model="data.item.price"
                    class="border-0"
                    :disabled="!!data.item.is_free"
                  />
                </b-input-group>
              </b-form-group>
            </template>
            <template slot="sku" slot-scope="data">
              <b-form-group
                class="mb-0"
                :state="validateState(categoryIndex, 'sku', data.index)"
                :invalid-feedback="
                  formErrors[categoryIndex].add_ons[data.index].sku
                "
              >
                <b-form-input
                  :id="`add-on-option-sku-${data.index}`"
                  v-model="data.item.sku"
                >
                </b-form-input>
              </b-form-group>
            </template>
            <template slot="visibility" slot-scope="data">
              <switches
                :id="`add-on-option-visibility-${data.index}`"
                v-model="data.item.visibility"
                theme="custom"
                color="primary"
                type-bold="true"
                style="top: 4px"
                :emit-on-mount="false"
              />
            </template>
            <template slot="is_free" slot-scope="data">
              <switches
                :id="`add-on-option-is-free-${data.index}`"
                v-model="data.item.is_free"
                theme="custom"
                color="primary"
                type-bold="true"
                style="top: 4px"
                :emit-on-mount="false"
              />
            </template>
          </b-table>
        </b-form-group>
        <b-form-group>
          <b-button
            :id="`add-add-on-option-button-${categoryIndex}`"
            variant="outline-primary"
            class="add-option-button mt-2 d-none"
            @click="addOption(categoryIndex)"
          >
            + Add Option
          </b-button>
        </b-form-group>
      </b-form>
      <b-button
        variant="outline-primary"
        class="add-option-button mb-5 d-none"
        @click="addAddOn"
      >
        + Create Add-On
      </b-button>
    </div>
    <template slot="modal-footer">
      <b-button
        variant="ghost"
        class="btn-cancel"
        @click="closeModal"
        :disabled="loading"
      >
        Cancel
      </b-button>
      <b-button variant="primary" @click="handleSaveAddOns" :disabled="loading"
        >Save</b-button
      >
    </template>
  </b-modal>
</template>

<script>
import Switches from "vue-switches";
import { required, requiredIf } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import _ from "lodash";
import CurrencyAmountInput from "@/components/common/CurrencyAmountInput";

export default {
  props: ["product"],
  components: { Switches, CurrencyAmountInput },
  data() {
    return {
      maxlengths: {
        category: 30,
        option: 30,
      },
      fields: [
        {
          key: "name",
          thClass: "border-top-0 border-bottom-0 border-right p-2",
          tdClass: "border-right p-2 align-middle",
        },
        {
          key: "price",
          thClass: "border-top-0 border-bottom-0 border-right p-2",
          tdClass: "border-right p-2 align-middle",
        },
        {
          key: "sku",
          label: "SKU",
          thClass: "border-top-0 border-bottom-0 border-right p-2 d-none",
          tdClass: "border-right p-2 align-middle d-none",
        },
        {
          key: "visibility",
          thClass: "border-top-0 border-bottom-0 border-right p-2",
          tdClass: "border-right text-center px-2 align-middle pt-4",
        },
        {
          key: "is_free",
          thClass: "border-top-0 border-bottom-0 p-2",
          tdClass: "text-center px-2 align-middle pt-4",
        },
      ],
    };
  },
  computed: {
    ...mapState("products", ["loading", "errors"]),
    addOns() {
      if (this.product) return this.product.add_on_category;
      else return [];
    },
    formErrors() {
      const validation_messages = {
        required: "This field is required.",
      };

      let errors = this.addOns.map((category) => ({
        name: null,
        limit: null,
        add_ons: category.add_ons.map(() => ({
          name: null,
          price: null,
          sku: null,
        })),
      }));

      const _v = this.$v.addOns;

      const isDirtyAndInvalid = (categoryIndex, field, optionIndex = null) => {
        if (optionIndex === null)
          return (
            _v.$each[categoryIndex][field].$dirty &&
            !_v.$each[categoryIndex][field].required
          );
        else
          return (
            _v.$each[categoryIndex].add_ons.$each[optionIndex][field].$dirty &&
            !_v.$each[categoryIndex].add_ons.$each[optionIndex][field].required
          );
      };

      const add_ons_fields = ["name", "price", "sku"];

      this.addOns.forEach((category, categoryIndex) => {
        if (isDirtyAndInvalid(categoryIndex, "name"))
          errors[categoryIndex].name = validation_messages.required;
        if (isDirtyAndInvalid(categoryIndex, "limit"))
          errors[categoryIndex].limit = validation_messages.required;

        category.add_ons.forEach((option, optionIndex) => {
          Object.keys(option).forEach((key) => {
            if (add_ons_fields.includes(key)) {
              if (isDirtyAndInvalid(categoryIndex, key, optionIndex))
                errors[categoryIndex].add_ons[optionIndex][key] =
                  validation_messages.required;
            }
          });
        });
      });

      return errors;
    },
  },
  validations: {
    addOns: {
      $each: {
        name: {
          required,
        },
        limit: {
          required: requiredIf((item) => item.allow_multiple_selection),
        },
        add_ons: {
          $each: {
            name: {
              required,
            },
            price: {
              required: requiredIf((item) => {
                return !item.is_free;
              }),
            },
            sku: {
              required,
            },
          },
        },
      },
    },
  },
  methods: {
    closeModal() {
      this.$parent.product = null;
      this.clearFormErrors();
      this.$root.$emit("bv::hide::modal", "inventory-view-add-ons-modal");
    },
    clearFormErrors() {
      this.$v.addOns.$reset();
    },
    addOption(categoryIndex) {
      this.addOns[categoryIndex].add_ons.push({
        uuid: null,
        name: null,
        price: null,
        sku: null,
        visibility: true,
        is_free: false,
      });
    },
    validateState(categoryIndex, field, optionIndex = null) {
      if (optionIndex !== null) {
        if (this.formErrors[categoryIndex].add_ons[optionIndex][field])
          return false;

        const { $dirty, $error } =
          this.$v.addOns.$each[categoryIndex].add_ons.$each[optionIndex][field];

        return $dirty ? !$error : null;
      } else {
        if (this.formErrors[categoryIndex][field]) return false;

        const { $dirty, $error } = this.$v.addOns.$each[categoryIndex][field];

        return $dirty ? !$error : null;
      }
    },
    addAddOn() {
      this.addOns.push({
        uuid: null,
        name: null,
        limit: 1,
        add_ons: [
          {
            uuid: null,
            name: null,
            price: null,
            sku: null,
            visibility: true,
            is_free: false,
          },
        ],
      });
    },
    handleSaveAddOns() {
      this.$v.addOns.$touch();

      if (!this.$v.addOns.$invalid) this.saveAddOns();
    },
    async saveAddOns() {
      const data = this.addOns.map((item) => {
        const add_ons = item.add_ons.map((addOn) => {
          let price = addOn.price ? addOn.price : 0;

          if (typeof price === "string")
            price =parseFloat(addOn.price.replaceAll(",", ""));

          return {
            uuid: addOn.uuid,
            option_name: addOn.name,
            currency: addOn.currency,
            is_visible: Boolean(addOn.visibility),
            is_free: Boolean(addOn.is_free),
            price,
          };
        });

        return {
          product_add_on_category_id: item.id,
          add_on_name: item.name,
          limit: item.allow_multiple_selection ? item.limit : 1,
          add_ons,
        };
      });

      await this.$store.dispatch("products/updateAddOns", { data });

      if (_.isEmpty(this.errors)) {
        this.$root.$emit("getInventoryList");
        this.closeModal();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("~@/assets/scss/products/_form.scss");
#form-container {
  min-height: inherit;
}
.add-option-button {
  border-radius: 20px;
}
</style>

<style lang="scss">
.add-on-options {
  .table {
    margin-bottom: 0;
  }
  thead {
    th {
      font-size: 14px;
    }
  }
}
</style>
